import React, { useState, useEffect, useRef } from 'react';
import './TaskTracking.css';
import { FiEye, FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { Tooltip } from 'react-tooltip';
import Select from 'react-select';

const TaskTracking = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const today = new Date().toISOString().split('T')[0];
  const [fromDateFilter, setFromDateFilter] = useState(today);
  const [toDateFilter, setToDateFilter] = useState(today);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [subsectionFilter, setSubsectionFilter] = useState(null);
  const token = localStorage.getItem('auth-token');
  const [tasks, setTasks] = useState([]);

  const modalRef = useRef(null);

  const copyToClipboard = (text) => {
    if (!text) return;
    try {
      navigator.clipboard?.writeText(String(text));
    } catch (e) {/* noop */ }
  };

  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
      if (e.key === 'Tab') {
        const focusables = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    setTimeout(() => {
      modalRef.current?.querySelector('[data-autofocus]')?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKey);
    };
  }, [modalOpen]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL_API_ACTIVTY_TRACK}?startDate=${fromDateFilter}&endDate=${toDateFilter}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      const result = await response.json();
      if (result.success && result.data.activityLogs) {
        const formattedData = result.data.activityLogs.map(log => {
          let parsedPayload = {};
          try {
            parsedPayload = JSON.parse(log.payload_json || "{}");
          } catch (e) {
            console.error("Invalid payload_json", e);
          }

          return {
            id: log.id,
            username: log.loginUserName,
            role: Array.isArray(log.roles) && log.roles.length > 0
              ? log.roles.join(', ')
              : 'N/A',
            affectedUserName: log.affectedUserName || null,
            affectedId: log.affectedId || null,
            affectedUserId: log.affectedUserId || null,
            section: log.section,
            subsection: log.subsection,
            action: (log.action || '').toLowerCase(),
            Date: new Date(log.createdAt).toISOString().split('T')[0],
            time: new Date(log.createdAt).toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }),
            payload: parsedPayload
          };
        });

        setTasks(formattedData);
      } else {
        console.error("Failed to fetch activity logs", result);
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDateFilter, toDateFilter]);

  const refreshData = () => {
    fetchData();
  };

  const handleSort = (key) => {
    const normalizedKey = key === 'date' ? 'Date' : key;
    let direction = 'asc';
    if (sortConfig.key === normalizedKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: normalizedKey, direction });
  };

  const subsectionOptions = sectionFilter
    ? Array.from(
      new Set(
        tasks
          .filter(task => task.section === sectionFilter.value)
          .map(task => task.subsection)
      )
    ).map(subsection => ({
      label: subsection,
      value: subsection
    }))
    : [];

  const sortedTasks = [...tasks].sort((a, b) => {
    if (!sortConfig.key) {
      return new Date(b.Date + ' ' + b.time) - new Date(a.Date + ' ' + a.time);
    }

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'date' || sortConfig.key === 'Date') {
      aValue = new Date(a.Date + ' ' + a.time);
      bValue = new Date(b.Date + ' ' + b.time);
    } else if (sortConfig.key === 'time') {
      aValue = new Date(a.Date + ' ' + a.time);
      bValue = new Date(b.Date + ' ' + b.time);
    } else {
      aValue = (aValue ?? '').toString().toLowerCase();
      bValue = (bValue ?? '').toString().toLowerCase();
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });


  const sectionOptions = Array.from(new Set(tasks.map(task => task.section))).map(section => ({
    label: section,
    value: section
  }));

  const roleOptions = Array.from(new Set(tasks.map(task => task.role))).map(role => ({
    label: role,
    value: role
  }));

  const filteredTasks = sortedTasks.filter(task => {
    const matchesSearch = Object.values(task).some(value =>
      (typeof value === 'string' || typeof value === 'number') &&
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesSection = !sectionFilter || task.section === sectionFilter.value;
    const matchesRole = !roleFilter || task.role === roleFilter.value;
    const matchesSubsection = !subsectionFilter || task.subsection === subsectionFilter.value;
    return (
      matchesSearch &&
      matchesSection &&
      matchesSubsection &&
      matchesRole
    );
  });

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredTasks.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredTasks.length / rowsPerPage);

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;
    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const getActionClass = (action) => {
    switch (action) {
      case 'created': return 'status-completed';
      case 'updated': return 'status-in-progress';
      case 'deleted': return 'status-pending';
      default: return 'status-in-review';
    }
  };

  const handleItemsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const tableHeaders = [
    { label: 'Username', key: 'username' },
    { label: 'Role', key: 'role' },
    { label: 'Section', key: 'section' },
    { label: 'Subsection', key: 'subsection' },
    { label: 'Action', key: 'action' },
    { label: 'Date', key: 'Date' },
    { label: 'Time', key: 'time' },
  ];

  return (
    <div className="enrollment-container">

      <div className="sales-filters-login">
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${sectionFilter ? "has-value" : ""}`}
            classNamePrefix="react-select"
            id="sectionFilter"
            options={sectionOptions}
            value={sectionFilter}
            onChange={(value) => {
              setSectionFilter(value);
              setSubsectionFilter(null);
              setCurrentPage(1);
            }}
            placeholder="All Sections"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {sectionFilter && <label htmlFor="sectionFilter">Section</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${subsectionFilter ? "has-value" : ""}`}
            classNamePrefix="react-select"
            id="subsectionFilter"
            options={subsectionOptions}
            value={subsectionFilter}
            onChange={(value) => {
              setSubsectionFilter(value);
              setCurrentPage(1);
            }}
            placeholder="All Subsections"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {subsectionFilter && <label htmlFor="subsectionFilter">Subsection</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${roleFilter ? "has-value" : ""}`}
            classNamePrefix="react-select"
            id="roleFilter"
            options={roleOptions}
            value={roleFilter}
            onChange={(value) => {
              setRoleFilter(value);
              setCurrentPage(1);
            }}
            placeholder="All Roles"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {roleFilter && <label htmlFor="roleFilter">Role</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${fromDateFilter ? "has-value" : ""}`}
            value={fromDateFilter}
            onChange={(e) => setFromDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            id="fromDateFilter"
          />
          {fromDateFilter && <label htmlFor="fromDateFilter">From Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${toDateFilter ? "has-value" : ""}`}
            value={toDateFilter}
            onChange={(e) => setToDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            id="toDateFilter"
          />
          {toDateFilter && <label htmlFor="toDateFilter">To Date</label>}
        </div>

      </div>

      <div className="tt-controls">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        <div className="items-and-dues">
          <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
            <option value={10}>10 items per page</option>
            <option value={50}>50 items per page</option>
            <option value={75}>75 items per page</option>
            <option value={100}>100 items per page</option>
          </select>
          <div className="pagination-selector">
            <div className="button-group">
              <div className="group-label">Items per page:</div>
              <div className="button-options">
                {[10, 50, 75, 100].map((option) => (
                  <button
                    key={option}
                    onClick={() =>
                      handleItemsPerPageChange({ target: { value: option } })
                    }
                    className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                  >
                    <span className="button-text">{option}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button className="tt-action-btn" onClick={refreshData}>
            <FiRefreshCw className={`refresh-icon ${isLoading ? 'spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="tt-table-container">
        {isLoading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        ) : (
          <>
            <table className="tt-table">
              <thead>
                <tr>
                  {tableHeaders.map(({ label, key }) => (
                    <th key={key} onClick={() => handleSort(key)}>
                      <div className="th-content">
                        <span>{label}</span>
                        <FiChevronDown
                          className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                        />
                      </div>
                    </th>
                  ))}
                  <th>View</th>
                </tr>
              </thead>

              <tbody>
                {currentRows.map(task => (
                  <tr key={task.id} className={selectedRows.includes(task.id) ? 'selected' : ''}>
                    <td>{task.username}</td>
                    <td>{task.role}</td>
                    <td>{task.section}</td>
                    <td>{task.subsection}</td>
                    <td><span className={`status-badge ${getActionClass(task.action)}`}>{task.action}</span></td>
                    <td>{task.Date}</td>
                    <td>{task.time}</td>
                    <td>
                      <button
                        className="tt-view-btn"
                        onClick={() => {
                          setSelectedTask(task);
                          setModalOpen(true);
                        }}
                        aria-label="View task details"
                      >
                        <FiEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

            </table>

            {filteredTasks.length === 0 && (
              <div className="tt-empty-state">
                <p>No tasks found</p>
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && selectedTask && (
        <div
          className="tt-modal-overlay"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tt-modal-title"
          aria-describedby="tt-modal-desc"
        >
          <div
            className="tt-modal-content"
            onClick={(e) => e.stopPropagation()}
            ref={modalRef}
          >
            {/* Header */}
            <div className="tt-modal-header">
              <h2 className="tt-modal-title" id="tt-modal-title">Task Details</h2>

              <span
                className={`tt-chip ${selectedTask.action === 'created'
                    ? 'success'
                    : selectedTask.action === 'deleted'
                      ? 'danger'
                      : selectedTask.action === 'updated'
                        ? 'warning'
                        : 'info'
                  }`}
                title={`Action: ${selectedTask.action}`}
              >
                <span className="dot"></span>
                {selectedTask.action}
              </span>

              <span className="tt-chip" title={`${selectedTask.Date} • ${selectedTask.time}`}>
                🗓 {selectedTask.Date} • {selectedTask.time}
              </span>

              {(selectedTask.affectedUserName || selectedTask.affectedId) && (
                <button
                  type="button"
                  className="tt-chip clickable"
                  onClick={() => copyToClipboard(selectedTask.affectedId)}
                  title="Click to copy affected ID"
                >
                  🧩 {selectedTask.affectedUserName || 'Affected'} — {selectedTask.affectedId ?? 'N/A'}
                </button>
              )}
            </div>

            {/* Body */}
            <div className="tt-modal-body" id="tt-modal-desc">
              {/* Key details grid */}
              <div className="tt-kv-grid">
                <div className="tt-kv">
                  <div className="k">Username</div>
                  <div className="v">{selectedTask.username}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Role</div>
                  <div className="v">{selectedTask.role}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Section</div>
                  <div className="v">{selectedTask.section}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Subsection</div>
                  <div className="v">{selectedTask.subsection}</div>
                </div>
                {(!selectedTask.payload || Object.keys(selectedTask.payload).length === 0) && (selectedTask.affectedUserName || selectedTask.affectedUserId) && (
                  <div className="tt-kv">
                    <div className="k">Affected</div>
                    <div className="v">{selectedTask.affectedUserName || 'N/A'} — {selectedTask.affectedUserId || 'N/A'}</div>
                  </div>
                )}
              </div>

              {/* Payload / Diff */}
              {selectedTask.action === "created" && selectedTask.payload?.new && (
                <>
                  <div className="tt-section-title">Payload</div>
                  <div className="diff-toolbar">
                    <button
                      className="tt-btn"
                      onClick={() => copyToClipboard(JSON.stringify(selectedTask.payload.new, null, 2))}
                      data-autofocus
                    >
                      Copy JSON
                    </button>
                  </div>
                  <div className="diff-panel">
                    <h4>New</h4>
                    <pre className="diff-json">
                      {Object.keys(selectedTask.payload.new).map((key) => {
                        let afterValue = selectedTask.payload.new[key];
                        if (typeof afterValue === "object") {
                          afterValue = JSON.stringify(afterValue, null, 2);
                        }
                        return (
                          <div key={key} className="diff-line added">
                            "{key}": "{afterValue}"
                          </div>
                        );
                      })}
                    </pre>
                  </div>
                </>
              )}

              {selectedTask.action !== "created" && selectedTask.payload?.old && selectedTask.payload?.new && (
                <>
                  <div className="tt-section-title">Before vs After</div>
                  <div className="diff-toolbar">
                    <button
                      className="tt-btn"
                      onClick={() => copyToClipboard(JSON.stringify(selectedTask.payload.old, null, 2))}
                    >
                      Copy "Before"
                    </button>
                    <button
                      className="tt-btn"
                      onClick={() => copyToClipboard(JSON.stringify(selectedTask.payload.new, null, 2))}
                    >
                      Copy "After"
                    </button>
                  </div>

                  <div className="diff-split-view">
                    {/* OLD JSON */}
                    <div className="diff-panel">
                      <h4>Before</h4>
                      <pre className="diff-json">
                        {Object.keys(selectedTask.payload.old).map((key) => {
                          let beforeValue = selectedTask.payload.old[key];
                          let afterValue = selectedTask.payload.new[key];
                          const changed = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
                          if (typeof beforeValue === "object") {
                            beforeValue = JSON.stringify(beforeValue, null, 2);
                          }
                          return (
                            <div key={key} className={`diff-line ${changed ? "removed" : ""}`}>
                              "{key}": "{beforeValue}"
                            </div>
                          );
                        })}
                      </pre>
                    </div>

                    {/* NEW JSON */}
                    <div className="diff-panel">
                      <h4>After</h4>
                      <pre className="diff-json">
                        {Object.keys(selectedTask.payload.new).map((key) => {
                          let beforeValue = selectedTask.payload.old[key];
                          let afterValue = selectedTask.payload.new[key];
                          const changed = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
                          if (typeof afterValue === "object") {
                            afterValue = JSON.stringify(afterValue, null, 2);
                          }
                          return (
                            <div key={key} className={`diff-line ${changed ? "added" : ""}`}>
                              "{key}": "{afterValue}"
                            </div>
                          );
                        })}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="tt-modal-footer">
              <button className="tt-modal-close-btn" onClick={() => setModalOpen(false)} data-autofocus>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLoading && filteredTasks.length > 0 && (
        <div className="pagination">
          <div className="showing-entries">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredTasks.length)} of {filteredTasks.length} entries
          </div>
          <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
          {generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
              <button
                key={index}
                className={`page-number ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            ) : (
              <span key={index} className="pagination-dots">{page}</span>
            )
          )}
          <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      <Tooltip id="filter-tooltip" place="top" content="Filter tasks" />
    </div>
  );
};

export default TaskTracking;
